import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useLanguage } from '../../contexts/LanguageContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Plus, Pencil, Trash2, Scissors, Droplets, Wind, List } from 'lucide-react';
import { toast } from 'sonner';
import ImageUpload from '../../components/ImageUpload';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyForm = {
  category_id: '', subcategory_id: '', name_en: '', name_ar: '', icon: '', image: '',
  laundry_price: 0, dry_clean_price: 0, pressing_price: 0,
  service_types: [],
  is_active: true, sort_order: 0
};

export default function ProductsPage() {
  const { t, lang } = useLanguage();
  const [priceLists, setPriceLists] = useState([]);
  const [selectedPriceList, setSelectedPriceList] = useState('');
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [filteredSubcategories, setFilteredSubcategories] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterSubcategory, setFilterSubcategory] = useState('all');

  // Load price lists on mount
  useEffect(() => {
    axios.get(`${API}/price-lists`).then(res => {
      setPriceLists(res.data);
      const defaultPL = res.data.find(p => p.is_default);
      if (defaultPL) setSelectedPriceList(defaultPL.id);
      else if (res.data.length > 0) setSelectedPriceList(res.data[0].id);
    });
  }, []);

  const loadData = useCallback(() => {
    if (!selectedPriceList) return;
    let productsUrl = `${API}/products?price_list_id=${selectedPriceList}`;
    if (filterCategory !== 'all') productsUrl += `&category_id=${filterCategory}`;
    if (filterSubcategory !== 'all') productsUrl += `&subcategory_id=${filterSubcategory}`;

    Promise.all([
      axios.get(productsUrl),
      axios.get(`${API}/categories?price_list_id=${selectedPriceList}`),
      axios.get(`${API}/subcategories?price_list_id=${selectedPriceList}`)
    ]).then(([prodRes, catRes, subRes]) => {
      setProducts(prodRes.data);
      setCategories(catRes.data);
      setSubcategories(subRes.data);
    }).catch(console.error);
  }, [selectedPriceList, filterCategory, filterSubcategory]);

  useEffect(() => { loadData(); }, [loadData]);

  // Filter subcategories based on selected category
  useEffect(() => {
    if (filterCategory === 'all') {
      setFilteredSubcategories(subcategories);
      setFilterSubcategory('all');
    } else {
      setFilteredSubcategories(subcategories.filter(s => s.category_id === filterCategory));
      setFilterSubcategory('all');
    }
  }, [filterCategory, subcategories]);

  // Filter subcategories for form
  const formSubcategories = form.category_id 
    ? subcategories.filter(s => s.category_id === form.category_id) 
    : [];

  const getName = (item) => lang === 'ar' ? item.name_ar : item.name_en;
  const formatPrice = (price) => price > 0 ? `${Number(price).toFixed(2)}` : '-';

  const getCategoryName = (catId) => {
    const cat = categories.find(c => c.id === catId);
    return cat ? getName(cat) : '-';
  };

  const getSubcategoryName = (subId) => {
    const sub = subcategories.find(s => s.id === subId);
    return sub ? getName(sub) : '-';
  };

  const getServiceLabel = (type) => {
    const labels = { 
      regular_wash: t('regularWash'), 
      dry_clean: t('dryClean'), 
      pressing: t('pressing') 
    };
    return labels[type] || type;
  };

  const openAdd = () => { 
    setEditing(null); 
    setForm({ 
      ...emptyForm, 
      category_id: categories[0]?.id || '',
      subcategory_id: ''
    }); 
    setDialogOpen(true); 
  };

  const openEdit = (prod) => {
    setEditing(prod);
    setForm({
      category_id: prod.category_id, 
      subcategory_id: prod.subcategory_id || '',
      name_en: prod.name_en, 
      name_ar: prod.name_ar,
      icon: prod.icon || '', 
      image: prod.image || '',
      laundry_price: prod.laundry_price || 0, 
      dry_clean_price: prod.dry_clean_price || 0,
      pressing_price: prod.pressing_price || 0,
      service_types: prod.service_types || [], 
      is_active: prod.is_active, 
      sort_order: prod.sort_order
    });
    setDialogOpen(true);
  };

  const openDelete = (prod) => { setToDelete(prod); setDeleteDialogOpen(true); };

  const handleCategoryChange = (catId) => {
    setForm(p => ({ ...p, category_id: catId, subcategory_id: '' }));
  };

  const handleSave = async () => {
    if (!form.name_en.trim() || !form.name_ar.trim() || !form.category_id || !form.subcategory_id) {
      toast.error('Please fill all required fields');
      return;
    }
    setSaving(true);
    try {
      // Auto-detect service types based on prices
      const serviceTypes = [];
      const laundryPrice = parseFloat(form.laundry_price) || 0;
      const dryCleanPrice = parseFloat(form.dry_clean_price) || 0;
      const pressingPrice = parseFloat(form.pressing_price) || 0;
      
      if (laundryPrice > 0) serviceTypes.push('regular_wash');
      if (dryCleanPrice > 0) serviceTypes.push('dry_clean');
      if (pressingPrice > 0) serviceTypes.push('pressing');

      const payload = {
        ...form,
        price_list_id: selectedPriceList,
        laundry_price: laundryPrice,
        dry_clean_price: dryCleanPrice,
        pressing_price: pressingPrice,
        service_types: serviceTypes,
        sort_order: parseInt(form.sort_order) || 0
      };
      
      if (editing) {
        await axios.put(`${API}/products/${editing.id}`, payload);
      } else {
        await axios.post(`${API}/products`, payload);
      }
      toast.success(t('savedSuccess'));
      setDialogOpen(false);
      loadData();
    } catch {
      toast.error(t('errorOccurred'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!toDelete) return;
    try {
      await axios.delete(`${API}/products/${toDelete.id}`);
      toast.success(t('deletedSuccess'));
      setDeleteDialogOpen(false);
      setToDelete(null);
      loadData();
    } catch {
      toast.error(t('errorOccurred'));
    }
  };

  return (
    <div className="fade-in" data-testid="products-admin-page">
      {/* Price List Selector */}
      <div className="flex items-center gap-4 mb-6 p-4 bg-slate-50 rounded-lg">
        <List className="h-5 w-5 text-[#0056A6]" />
        <div className="flex-1">
          <Label className="text-sm text-slate-600">{t('selectPriceList')}</Label>
          <select 
            value={selectedPriceList} 
            onChange={(e) => setSelectedPriceList(e.target.value)}
            className="mt-1 flex h-9 w-full max-w-xs items-center justify-between rounded-md border border-input bg-white px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring"
            data-testid="price-list-select"
          >
            {priceLists.map(pl => (
              <option key={pl.id} value={pl.id}>{lang === 'ar' ? pl.name_ar : pl.name_en}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-6">
        <h1 className="text-2xl font-bold text-slate-900">{t('manageProducts')}</h1>
        <div className="flex flex-wrap items-center gap-3">
          <select 
            value={filterCategory} 
            onChange={(e) => setFilterCategory(e.target.value)}
            className="flex h-9 w-40 items-center justify-between rounded-md border border-input bg-white px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring"
            data-testid="filter-category-select"
          >
            <option value="all">{t('allCategories')}</option>
            {categories.map(cat => (
              <option key={cat.id} value={cat.id}>{getName(cat)}</option>
            ))}
          </select>
          <select 
            value={filterSubcategory} 
            onChange={(e) => setFilterSubcategory(e.target.value)}
            className="flex h-9 w-40 items-center justify-between rounded-md border border-input bg-white px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring"
            data-testid="filter-subcategory-select"
          >
            <option value="all">{t('allServices')}</option>
            {filteredSubcategories.map(sub => (
              <option key={sub.id} value={sub.id}>{getName(sub)}</option>
            ))}
          </select>
          <Button onClick={openAdd} className="bg-[#0056A6] hover:bg-[#004E93] shadow-lg shadow-blue-500/20" data-testid="add-product-btn">
            <Plus className="h-4 w-4 me-1.5" />
            {t('addProduct')}
          </Button>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-100 shadow-[0_2px_8px_rgba(0,0,0,0.04)] overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50/50">
              <TableHead>{t('nameEn')}</TableHead>
              <TableHead>{t('category')}</TableHead>
              <TableHead>{t('subcategory')}</TableHead>
              <TableHead className="text-center">
                <div className="flex items-center justify-center gap-1">
                  <Droplets className="h-3 w-3 text-blue-500" />
                  {t('regularWash')}
                </div>
              </TableHead>
              <TableHead className="text-center">
                <div className="flex items-center justify-center gap-1">
                  <Wind className="h-3 w-3 text-emerald-500" />
                  {t('dryClean')}
                </div>
              </TableHead>
              <TableHead className="text-center">
                <div className="flex items-center justify-center gap-1">
                  <Scissors className="h-3 w-3 text-amber-500" />
                  {t('pressing')}
                </div>
              </TableHead>
              <TableHead className="text-center">{t('status')}</TableHead>
              <TableHead className="text-end">{t('actions')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {products.map(prod => (
              <TableRow key={prod.id} data-testid={`product-row-${prod.id}`}>
                <TableCell>
                  <div>
                    <span className="font-medium text-slate-800">{prod.name_en}</span>
                    <span className="block text-xs text-slate-400">{prod.name_ar}</span>
                  </div>
                </TableCell>
                <TableCell className="text-sm text-slate-600">{getCategoryName(prod.category_id)}</TableCell>
                <TableCell className="text-sm text-slate-500">{getSubcategoryName(prod.subcategory_id)}</TableCell>
                <TableCell className="text-center font-semibold text-blue-600">{formatPrice(prod.laundry_price)}</TableCell>
                <TableCell className="text-center font-semibold text-emerald-600">{formatPrice(prod.dry_clean_price)}</TableCell>
                <TableCell className="text-center font-semibold text-amber-600">{formatPrice(prod.pressing_price)}</TableCell>
                <TableCell className="text-center">
                  <Badge variant={prod.is_active ? "default" : "secondary"} className={prod.is_active ? "bg-emerald-100 text-emerald-700 border-0" : "bg-slate-100 text-slate-500 border-0"}>
                    {prod.is_active ? t('active') : t('inactive')}
                  </Badge>
                </TableCell>
                <TableCell className="text-end">
                  <div className="flex items-center justify-end gap-1">
                    <Button variant="ghost" size="sm" onClick={() => openEdit(prod)} data-testid={`edit-product-${prod.id}`}>
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-600" onClick={() => openDelete(prod)} data-testid={`delete-product-${prod.id}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {products.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-8 text-slate-400">{t('noProducts')}</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="product-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? t('editProduct') : t('addProduct')}</DialogTitle>
            <DialogDescription>{editing ? t('editProduct') : t('addProduct')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <ImageUpload
              label={t('image')}
              value={form.image}
              onChange={(url) => setForm(p => ({ ...p, image: url }))}
            />
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{t('category')} *</Label>
                <select 
                  value={form.category_id} 
                  onChange={(e) => handleCategoryChange(e.target.value)}
                  className="mt-1.5 flex h-9 w-full items-center justify-between rounded-md border border-input bg-white px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring"
                  data-testid="product-category-select"
                >
                  <option value="">{t('selectCategory')}</option>
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.id}>{getName(cat)}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label>{t('subcategory')} *</Label>
                <select 
                  value={form.subcategory_id} 
                  onChange={(e) => setForm(p => ({ ...p, subcategory_id: e.target.value }))}
                  disabled={!form.category_id}
                  className="mt-1.5 flex h-9 w-full items-center justify-between rounded-md border border-input bg-white px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="product-subcategory-select"
                >
                  <option value="">{t('selectSubcategory')}</option>
                  {formSubcategories.map(sub => (
                    <option key={sub.id} value={sub.id}>{getName(sub)}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{t('nameEn')} *</Label>
                <Input value={form.name_en} onChange={e => setForm(p => ({ ...p, name_en: e.target.value }))} className="mt-1.5" data-testid="product-name-en" />
              </div>
              <div>
                <Label>{t('nameAr')} *</Label>
                <Input value={form.name_ar} onChange={e => setForm(p => ({ ...p, name_ar: e.target.value }))} className="mt-1.5" dir="rtl" data-testid="product-name-ar" />
              </div>
            </div>
            
            {/* Pricing Section */}
            <div className="p-4 bg-slate-50 rounded-lg space-y-3">
              <Label className="text-sm font-medium text-slate-700 block text-center">{t('priceIncVat')}</Label>
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center">
                  <Label className="text-xs text-blue-600 flex items-center justify-center gap-1 mb-1">
                    <Droplets className="h-3 w-3 flex-shrink-0" />
                    {t('regularWash')}
                  </Label>
                  <Input 
                    type="number" 
                    step="0.01" 
                    min="0" 
                    value={form.laundry_price} 
                    onChange={e => setForm(p => ({ ...p, laundry_price: e.target.value }))} 
                    className="text-center" 
                    dir="ltr"
                    data-testid="product-laundry-price" 
                  />
                </div>
                <div className="text-center">
                  <Label className="text-xs text-emerald-600 flex items-center justify-center gap-1 mb-1">
                    <Wind className="h-3 w-3 flex-shrink-0" />
                    {t('dryClean')}
                  </Label>
                  <Input 
                    type="number" 
                    step="0.01" 
                    min="0" 
                    value={form.dry_clean_price} 
                    onChange={e => setForm(p => ({ ...p, dry_clean_price: e.target.value }))} 
                    className="text-center" 
                    dir="ltr"
                    data-testid="product-dry-clean-price" 
                  />
                </div>
                <div className="text-center">
                  <Label className="text-xs text-amber-600 flex items-center justify-center gap-1 mb-1">
                    <Scissors className="h-3 w-3 flex-shrink-0" />
                    {t('pressing')}
                  </Label>
                  <Input 
                    type="number" 
                    step="0.01" 
                    min="0" 
                    value={form.pressing_price} 
                    onChange={e => setForm(p => ({ ...p, pressing_price: e.target.value }))} 
                    className="text-center" 
                    dir="ltr"
                    data-testid="product-pressing-price" 
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{t('sortOrder')}</Label>
                <Input type="number" value={form.sort_order} onChange={e => setForm(p => ({ ...p, sort_order: e.target.value }))} className="mt-1.5" dir="ltr" data-testid="product-sort-order" />
              </div>
              <div className="flex items-end justify-end gap-3 pb-1">
                <Label className="text-sm">{form.is_active ? t('active') : t('inactive')}</Label>
                <Switch checked={form.is_active} onCheckedChange={v => setForm(p => ({ ...p, is_active: v }))} data-testid="product-active-switch" />
              </div>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDialogOpen(false)} data-testid="product-cancel-btn">{t('cancel')}</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-[#0056A6] hover:bg-[#004E93]" data-testid="product-save-btn">
              {saving ? <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" /> : t('save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="sm:max-w-sm" data-testid="delete-product-dialog">
          <DialogHeader>
            <DialogTitle>{t('deleteProduct')}</DialogTitle>
            <DialogDescription>{t('deleteConfirm')}</DialogDescription>
          </DialogHeader>
          <p className="text-sm text-slate-600">{toDelete && getName(toDelete)}</p>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>{t('cancel')}</Button>
            <Button variant="destructive" onClick={handleDelete} data-testid="delete-product-confirm-btn">{t('delete')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
